import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, type AdminCoupon } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SubmitButton } from "@/components/form/SubmitButton";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

type FormState = {
    id?: string;
    code: string;
    title: string;
    description: string;
    kind: "flat" | "percent" | "free_delivery";
    value: number;
    min_order: number;
    max_discount: number;
    usage_limit: string;
    expires_at: string;
    is_active: boolean;
};

function emptyForm(): FormState {
    return {
        code: "",
        title: "",
        description: "",
        kind: "flat",
        value: 0,
        min_order: 0,
        max_discount: 0,
        usage_limit: "",
        expires_at: "",
        is_active: true,
    };
}

export default function AdminCoupons() {
    const qc = useQueryClient();
    const { data = [], isLoading } = useQuery({
        queryKey: ["admin", "coupons"],
        queryFn: adminApi.listCoupons,
    });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const openNew = () => {
        setForm(emptyForm());
        setError(null);
        setDialogOpen(true);
    };

    const openEdit = (c: AdminCoupon) => {
        setForm({
            id: c.id,
            code: c.code,
            title: c.title,
            description: c.description,
            kind: c.kind,
            value: c.value,
            min_order: c.min_order,
            max_discount: c.max_discount,
            usage_limit: c.usage_limit == null ? "" : String(c.usage_limit),
            expires_at: c.expires_at || "",
            is_active: c.is_active,
        });
        setError(null);
        setDialogOpen(true);
    };

    const delMut = useMutation({
        mutationFn: (id: string) => adminApi.deleteCoupon(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "coupons"] });
            toast.success("Coupon deleted");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't delete coupon")),
    });

    const onSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSaving(true);
        try {
            const payload = {
                code: form.code,
                title: form.title,
                description: form.description,
                kind: form.kind,
                value: Number(form.value),
                min_order: Number(form.min_order),
                max_discount: Number(form.max_discount),
                usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
                expires_at: form.expires_at || null,
                is_active: form.is_active,
            };
            if (form.id) {
                await adminApi.updateCoupon(form.id, payload);
                toast.success("Coupon updated");
            } else {
                await adminApi.createCoupon(payload);
                toast.success("Coupon created");
            }
            qc.invalidateQueries({ queryKey: ["admin", "coupons"] });
            setDialogOpen(false);
        } catch (err) {
            setError(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-coupons-page" className="space-y-5">
            <div className="flex items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold sm:text-3xl">Coupons</h1>
                    <p className="text-sm text-muted-foreground">
                        {data.length} promotional codes
                    </p>
                </div>
                <button
                    onClick={openNew}
                    data-testid="admin-coupon-new"
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                >
                    <Plus className="h-4 w-4" /> New coupon
                </button>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Code</th>
                            <th className="px-4 py-3">Kind</th>
                            <th className="px-4 py-3">Value</th>
                            <th className="px-4 py-3">Min order</th>
                            <th className="px-4 py-3">Usage</th>
                            <th className="px-4 py-3">Expires</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                        {isLoading ? (
                            <tr>
                                <td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">
                                    Loading coupons…
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">
                                    No coupons yet.
                                </td>
                            </tr>
                        ) : (
                            data.map((c) => (
                                <tr key={c.id} data-testid={`admin-coupon-row-${c.id}`}>
                                    <td className="px-4 py-3">
                                        <p className="font-mono font-bold">{c.code}</p>
                                        <p className="text-[11px] text-muted-foreground">{c.title}</p>
                                    </td>
                                    <td className="px-4 py-3 text-xs uppercase">{c.kind.replace("_", " ")}</td>
                                    <td className="px-4 py-3">
                                        {c.kind === "percent" ? `${c.value}%` : c.kind === "free_delivery" ? "Free delivery" : `₹${c.value}`}
                                    </td>
                                    <td className="px-4 py-3">{c.min_order > 0 ? `₹${c.min_order}` : "None"}</td>
                                    <td className="px-4 py-3 text-xs">
                                        {c.used_count}
                                        {c.usage_limit ? ` / ${c.usage_limit}` : ""}
                                    </td>
                                    <td className="px-4 py-3 text-xs">{c.expires_at || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${
                                                c.is_active
                                                    ? "bg-emerald-500/10 text-emerald-700"
                                                    : "bg-slate-500/10 text-slate-500"
                                            }`}
                                        >
                                            {c.is_active ? "Live" : "Paused"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                            <button
                                                onClick={() => openEdit(c)}
                                                data-testid={`admin-coupon-edit-${c.id}`}
                                                className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (window.confirm(`Delete coupon ${c.code}?`)) delMut.mutate(c.id);
                                                }}
                                                data-testid={`admin-coupon-delete-${c.id}`}
                                                className="grid h-8 w-8 place-items-center rounded-lg text-error hover:bg-error/10"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="admin-coupon-dialog" className="sm:max-w-lg">
                    <form onSubmit={onSave}>
                        <DialogHeader>
                            <DialogTitle>{form.id ? "Edit coupon" : "New coupon"}</DialogTitle>
                        </DialogHeader>
                        <div className="mt-4 space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <Label>Code *</Label>
                                    <Input
                                        data-testid="admin-coupon-code"
                                        value={form.code}
                                        onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        required
                                        minLength={2}
                                    />
                                </div>
                                <div>
                                    <Label>Kind *</Label>
                                    <select
                                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                        data-testid="admin-coupon-kind"
                                        value={form.kind}
                                        onChange={(e) => setForm({ ...form, kind: e.target.value as FormState["kind"] })}
                                    >
                                        <option value="flat">Flat ₹</option>
                                        <option value="percent">Percent %</option>
                                        <option value="free_delivery">Free delivery</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <Label>Title *</Label>
                                <Input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Input
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                />
                            </div>
                            <div className="grid gap-3 sm:grid-cols-3">
                                <div>
                                    <Label>Value *</Label>
                                    <Input
                                        type="number"
                                        data-testid="admin-coupon-value"
                                        value={form.value}
                                        onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
                                        min={0}
                                    />
                                </div>
                                <div>
                                    <Label>Min order</Label>
                                    <Input
                                        type="number"
                                        value={form.min_order}
                                        onChange={(e) => setForm({ ...form, min_order: Number(e.target.value) })}
                                        min={0}
                                    />
                                </div>
                                <div>
                                    <Label>Max discount</Label>
                                    <Input
                                        type="number"
                                        value={form.max_discount}
                                        onChange={(e) => setForm({ ...form, max_discount: Number(e.target.value) })}
                                        min={0}
                                    />
                                </div>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <Label>Usage limit</Label>
                                    <Input
                                        type="number"
                                        placeholder="Unlimited"
                                        value={form.usage_limit}
                                        onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                                        min={0}
                                    />
                                </div>
                                <div>
                                    <Label>Expires</Label>
                                    <Input
                                        type="date"
                                        value={form.expires_at ? form.expires_at.slice(0, 10) : ""}
                                        onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                                    />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                />
                                Coupon is live
                            </label>
                            {error ? (
                                <p role="alert" data-testid="admin-coupon-error" className="text-xs font-medium text-error">
                                    {error}
                                </p>
                            ) : null}
                        </div>
                        <DialogFooter className="mt-4 gap-2">
                            <button
                                type="button"
                                onClick={() => setDialogOpen(false)}
                                className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <SubmitButton
                                type="submit"
                                fullWidth={false}
                                loading={saving}
                                data-testid="admin-coupon-save"
                            >
                                Save
                            </SubmitButton>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
